import React, { useCallback, useEffect, useState } from 'react';
import t from 'react-translate';
import { useSelector } from 'react-redux';
import { translationUpdate } from 'redux/actions/users';
import { Exercise } from 'redux/schemas/models/exercise';

import { useAppDispatch } from 'redux/store';
import { getCurrentUser } from 'redux/selectors/users';
import { TranslationInfo } from 'redux/schemas/models/edit-translations';
import { closeEditTranslationComponent } from 'redux/actions/edit-translations';
import { getLectureComponent } from 'redux/selectors/timeline';
import { ComponentType, NLectureComponent } from 'redux/schemas/models/lecture-component';
import { css } from '@emotion/react';
import { doubleSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';
import EditableTitleWithDescriptionStyled from './base/editable-title-and-description-styled';


type EditableProps = {
  editInfo: TranslationInfo,
  containerClass: string,
  onSaveRef: (fn: () => void) => void,
  setSavingDisabled: (boolean) => void,
};

const EditableAssignment = ({
  editInfo,
  containerClass,
  onSaveRef,
  setSavingDisabled,
}: EditableProps) => {
  const lectureComponent = useSelector((state) => (
    getLectureComponent(state, editInfo.id)
  )) as NLectureComponent<ComponentType.EXERCISE>;
  const exercise = useSelector((state) => state.models.exercises[lectureComponent.exercise]) as Exercise;
  const initialState = {
    activity: {
      title: exercise.title,
      description: exercise.description,
    },
    sections: exercise.template?.sections ?? [],
  };
  const [assignment, setAssignment] = useState(initialState);
  const currentUser = useSelector(getCurrentUser);
  const dispatch = useAppDispatch();

  const editableAssignmentStyle = css`
    width: 100%;
    .separator {
      margin: ${standardSpacing}px ${doubleSpacing}px ${standardSpacing}px;
    }
  `;

  const save = useCallback(() => {
    const attributes = [];
    const fields = Object.keys(assignment.activity);
    fields.forEach(field => {
      if (assignment.activity[field] !== initialState.activity[field]) {
        attributes.push({
          translationId: exercise.translationIds[field],
          name: field,
          text: assignment.activity[field],
          elementType: lectureComponent.type,
          elementId: exercise.id,
        });
      }
    });
    if (assignment.sections !== initialState.sections) {
      assignment.sections.forEach((section, index) => {
        const sectionFields = ['header', 'instructions'];
        sectionFields.forEach(sectionField => {
          if (section[sectionField] !== initialState.sections[index][sectionField]) {
            attributes.push({
              translationId: section.translationIds[sectionField],
              name: sectionField,
              text: section[sectionField],
              elementType: lectureComponent.type,
              elementId: index,
            });
          }
        });
      });
    }
    const payload = {
      id: lectureComponent.id,
      attributes,
    };
    dispatch(translationUpdate(payload)).then(() => {
      dispatch(closeEditTranslationComponent());
    });
  }, [assignment, currentUser.translationPreferenceLanguage, dispatch, editInfo]);

  useEffect(() => {
    onSaveRef(() => save);
  }, [onSaveRef, save]);

  useEffect(() => {
    if (initialState !== assignment) {
      setSavingDisabled(false);
    }
    return (() => {
      setSavingDisabled(true);
    });
  }, [assignment]);

  const setSection = (index, newSection) => {
    const newSections = [...assignment.sections];
    newSections[index] = {
      ...newSections[index],
      header: newSection.title,
      instructions: newSection.description,
    };
    setAssignment({
      ...assignment,
      sections: newSections,
    });
  };

  return (
    <div className='editable-assignment' css={editableAssignmentStyle}>
      <EditableTitleWithDescriptionStyled
        activity={assignment.activity}
        setActivity={(newActivity) => setAssignment({ ...assignment, activity: newActivity })}
        containerClass={containerClass}
        titleName={t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.TITLE()}
        descriptionName={t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.EXPECTATIONS()}
      />
      <div className='sections'>
        {
          assignment.sections.map((section, index) => (
            <div className='section'>
              <hr className='separator bg-gray-5' />
              <EditableTitleWithDescriptionStyled
                activity={{
                  title: section.header,
                  description: section.instructions,
                }}
                setActivity={(newSection) => setSection(index, newSection)}
                containerClass={containerClass}
                titleName={t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.SECTION_HEADING()}
                descriptionName={t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.SECTION_INSTRUCTIONS()}
              />
            </div>
          ))
        }
      </div>
    </div>
  );
};

export default EditableAssignment;
